import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, FileSpreadsheet, AlertTriangle, FileUp, Import, Bot, Download, Loader2 } from 'lucide-react';
import baseapi from '../../services/baseapi';
import { showError, showSuccess } from '../../utils/toast';
import SingleSelectApiDropdown from '../../components/SingleSelectApiDropdown';
import { useClientLevel } from '../../hooks/useClientLevel';

interface UploadCamerasModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const UploadModal: React.FC<UploadCamerasModalProps> = ({ open, onClose, onSuccess }) => {
    const [agent, setAgent] = useState<any | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [downloadingTemplate, setDownloadingTemplate] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const level = useClientLevel();
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!open) return;
        setAgent(null); setFile(null); setErrors({}); setDownloadingTemplate(false);
    }, [open]);

    const handleClose = () => { setAgent(null); setFile(null); setErrors({}); onClose(); };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const selectedFile = e.target.files[0];
            const valid = ['.xlsx', '.xls', '.csv'];
            const ext = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
            if (!valid.includes(ext)) { setErrors(prev => ({ ...prev, file: 'Invalid file format (.xlsx, .xls, .csv)' })); setFile(null); return; }
            setFile(selectedFile); setErrors(prev => { const next = { ...prev }; delete next.file; return next; });
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            setDownloadingTemplate(true);
            const res = await baseapi.get('/agent/assets/template', { responseType: 'blob' });
            const blob = new Blob([res.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a'); link.href = url;
            link.setAttribute('download', 'asset_template.xlsx');
            document.body.appendChild(link); link.click(); link.remove();
            window.URL.revokeObjectURL(url); showSuccess('Template downloaded');
        } catch { showError('Download failed'); }
        finally { setDownloadingTemplate(false); }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const next: Record<string, string> = {};
        if (!agent) next.agent = 'Select an agent'; if (!file) next.file = 'Select a file';
        setErrors(next); if (Object.keys(next).length > 0) return;

        try {
            setLoading(true);
            const fd = new FormData();
            fd.append('file', file as Blob); fd.append('agent_id', String(agent.id)); fd.append('client_id', String(level?.id));
            const response = await baseapi.post('/agent/assets/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            if (response.data.status) { showSuccess('Assets imported'); onSuccess(); handleClose(); }
            else showError(response.data.message || 'Import failed');
        } catch (error: any) { showError(error.response?.data?.message || error.message || 'Import failed'); }
        finally { setLoading(false); }
    };

    if (!open) return null;

    return (
        <>
            <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-[2px] z-[9998]" onClick={handleClose} />
            <div className="fixed inset-0 flex items-center justify-center z-[9999] p-4">
                <div className="w-full max-w-lg bg-white border border-slate-200 shadow-2xl rounded-2xl text-slate-900 relative overflow-hidden animate-in zoom-in-95 duration-200">
                    <div className="sticky top-0 bg-white/80 backdrop-blur-md border-b border-slate-100 px-6 py-4 z-10 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-[#422568] rounded-lg"><Import className="h-5 w-5 text-[#422568]" /></div>
                            <div>
                                <h2 className="text-sm font-black uppercase tracking-widest">Bulk Import</h2>
                                <p className="text-[10px] text-slate-500 font-bold uppercase tracking-tighter mt-1">Infrastructure Load</p>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={handleDownloadTemplate} disabled={downloadingTemplate} className="flex items-center gap-1.5 px-3 py-1.5 text-[9px] font-black uppercase tracking-widest bg-slate-50 border border-slate-200 text-slate-600 rounded-lg hover:border-[#422568] hover:text-[#422568] transition-all shadow-sm">
                                {downloadingTemplate ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Template
                            </button>
                            <button onClick={handleClose} className="text-slate-400 hover:text-slate-600 p-1.5 hover:bg-slate-50 rounded-full transition-all"><X className="w-5 h-5" /></button>
                        </div>
                    </div>

                    <div className="p-6 space-y-6">
                        <div>
                            <SingleSelectApiDropdown label="Assign to Probe Agent" value={agent} onChange={(item) => { setAgent(item); if (item) setErrors(p => ({ ...p, agent: '' })); }} apiEndpoint="/agents" icon={Bot} placeholder="Select target agent..." displayProperty="name" searchFields={['name']} required={true} additionalQueries={{ client_id: level.id, agent_type: 'probe' }} searchInputPlaceholder="Search agents..." />
                            {errors.agent && <p className="mt-1.5 text-[10px] font-bold text-rose-500 flex items-center"><AlertTriangle className="w-3 h-3 mr-1" /> {errors.agent}</p>}
                        </div>

                        <div>
                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-2"><FileSpreadsheet className="w-3.5 h-3.5 text-[#422568]" /> Data Source File <span className="text-rose-500">*</span></label>
                            <div onClick={() => fileInputRef.current?.click()} className={`relative cursor-pointer flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-2xl transition-all duration-300 ${file ? 'border-[#422568] bg-[#422568]' : errors.file ? 'border-rose-400 bg-rose-50' : 'border-slate-200 bg-slate-50 hover:border-[#422568]'}`}>
                                <input type="file" className="hidden" ref={fileInputRef} accept=".xlsx, .xls, .csv" onChange={handleFileChange} />
                                {file ? (
                                    <div className="flex flex-col items-center p-4">
                                        <div className="w-12 h-12 bg-white rounded-2xl shadow-sm border border-[#422568] flex items-center justify-center mb-3"><FileSpreadsheet className="w-6 h-6 text-[#422568]" /></div>
                                        <span className="text-[12px] font-black text-slate-900 truncate max-w-[200px]">{file.name}</span>
                                        <span className="text-[10px] text-slate-500 font-bold uppercase mt-1">{(file.size / 1024).toFixed(1)} KB</span>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center p-4">
                                        <div className="w-12 h-12 bg-white rounded-2xl border border-slate-100 flex items-center justify-center mb-3 shadow-sm"><FileUp className="w-6 h-6 text-slate-300" /></div>
                                        <span className="text-[12px] font-black text-slate-900">Select Spreadsheet</span>
                                        <span className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mt-1">XLSX, XLS, OR CSV</span>
                                    </div>
                                )}
                            </div>
                            {errors.file && <p className="mt-2 text-[10px] font-bold text-rose-500 flex items-center"><AlertTriangle className="w-3 h-3 mr-1" /> {errors.file}</p>}
                        </div>

                        <div className="flex justify-end gap-3 pt-6 border-t border-slate-100">
                            <button type="button" onClick={handleClose} disabled={loading} className="px-4 py-2 text-[11px] font-black text-slate-500 hover:text-slate-700 uppercase tracking-widest">Cancel</button>
                            <button type="button" onClick={handleSubmit} disabled={loading} className="px-6 py-2 bg-[#422568] text-white rounded-xl text-[11px] font-black shadow-lg shadow-[#422568]/20 hover:bg-[#422568] transition-all active:scale-95 flex items-center justify-center gap-2 uppercase tracking-widest min-w-[140px]">
                                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {loading ? 'Importing...' : 'Upload Data'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default UploadModal;
